<template>
  <div
    ref="carouselRoot"
    class="pricing-carousel fall"
    @mouseenter="stopAutoplay"
    @mouseleave="handlePointerLeave"
    @focusin="stopAutoplay"
    @focusout="handleCarouselFocusOut"
  >
    <div ref="carouselStage" class="pricing-carousel__stage">
      <article
        v-for="(offer, index) in offers"
        :key="offer.id"
        ref="carouselCards"
        class="pricing-tile pricing-carousel__card"
        :class="[
          offer.variant === 'primary'
            ? 'pricing-tile--primary'
            : 'pricing-tile--light',
          `pricing-carousel__card--${getCarouselState(index)}`,
        ]"
      >
        <header class="pricing-tile__header">
          <p class="pricing-tile__eyebrow">
            {{ offer.eyebrow || eyebrow }}
          </p>
          <div class="pricing-tile__title-row">
            <h3 class="pricing-tile__title">{{ offer.title }}</h3>
            <span class="pricing-tile__badge">{{ offer.badge }}</span>
          </div>
        </header>

        <div class="pricing-tile__price-box">
          <p class="pricing-tile__price">{{ offer.price }}<span>€</span></p>
          <p
            class="pricing-tile__fee"
            :class="{ 'pricing-tile__fee--placeholder': !offer.fee }"
          >
            {{ offer.fee || " " }}
          </p>
        </div>

        <ul class="pricing-tile__meta">
          <li v-for="detail in offer.meta" :key="`${offer.id}-${detail.label}`">
            <span class="pricing-tile__meta-label">{{ detail.label }}</span>
            <strong class="pricing-tile__meta-value">{{ detail.value }}</strong>
          </li>
        </ul>

        <div v-if="offer.note" class="pricing-tile__note-wrap">
          <div class="pricing-tile__note">
            <p class="pricing-tile__note-title">{{ offer.note.title }}</p>
            <p class="pricing-tile__note-copy">{{ offer.note.text }}</p>
          </div>
        </div>

        <ul
          v-if="offer.highlights && offer.highlights.length"
          class="pricing-tile__highlights"
        >
          <li
            v-for="highlight in offer.highlights"
            :key="`${offer.id}-${highlight}`"
          >
            {{ highlight }}
          </li>
        </ul>

        <footer class="pricing-tile__footer">
          <router-link :to="offer.route" class="pricing-tile__link">
            {{ offer.ctaLabel || "Voir le détail" }}
          </router-link>
        </footer>
      </article>
    </div>

    <div ref="carouselControls" class="pricing-carousel__controls">
      <button
        type="button"
        class="pricing-carousel__nav"
        aria-label="Carte précédente"
        @click="previousOffer"
      >
        &#8249;
      </button>
      <div class="pricing-carousel__dots">
        <button
          v-for="(offer, index) in offers"
          :key="`${offer.id}-dot`"
          type="button"
          class="pricing-carousel__dot"
          :class="{
            'pricing-carousel__dot--active': index === activeOfferIndex,
          }"
          :aria-label="`Afficher ${offer.title} ${offer.badge}`"
          @click="goToOffer(index)"
        ></button>
      </div>
      <button
        type="button"
        class="pricing-carousel__nav"
        aria-label="Carte suivante"
        @click="nextOffer"
      >
        &#8250;
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ServicePricingCarousel",
  props: {
    offers: {
      type: Array,
      required: true,
    },
    eyebrow: {
      type: String,
      default: "",
    },
    autoplayDelay: {
      type: Number,
      default: 4800,
    },
  },
  data() {
    return {
      activeOfferIndex: 0,
      autoplayTimer: null,
      isMobileViewport: false,
      handleResize: null,
      resizeObserver: null,
      toggleObserver: null,
    };
  },
  mounted() {
    this.syncViewportMode();
    this.handleResize = () => {
      this.syncViewportMode();
      this.refreshCarousel();
    };
    window.addEventListener("resize", this.handleResize);
    this.setupResizeObserver();
    this.setupToggleObserver();
    this.refreshCarousel();
  },
  beforeUnmount() {
    this.stopAutoplay();
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.toggleObserver) {
      this.toggleObserver.disconnect();
    }
  },
  methods: {
    syncViewportMode() {
      this.isMobileViewport = window.innerWidth <= 767;
    },
    isCarouselVisible() {
      const root = this.$refs.carouselRoot;
      return Boolean(root && root.offsetParent !== null);
    },
    setupResizeObserver() {
      const root = this.$refs.carouselRoot;

      if (!root || typeof ResizeObserver === "undefined") {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.syncViewportMode();
        this.refreshCarousel();
      });

      this.resizeObserver.observe(root);
    },
    setupToggleObserver() {
      const host = this.$el && this.$el.parentElement;

      if (
        !host ||
        !host.classList.contains("toggle-item") ||
        typeof MutationObserver === "undefined"
      ) {
        return;
      }

      this.toggleObserver = new MutationObserver(() => {
        this.$nextTick(() => {
          if (host.classList.contains("active")) {
            this.refreshCarousel();
            return;
          }

          this.stopAutoplay();
        });
      });

      this.toggleObserver.observe(host, {
        attributes: true,
        attributeFilter: ["class"],
      });
    },
    refreshCarousel() {
      this.$nextTick(() => {
        if (!this.isCarouselVisible()) {
          this.stopAutoplay();
          return;
        }

        this.startAutoplay();
      });
    },
    normalizeIndex(index) {
      const total = this.offers.length;
      return ((index % total) + total) % total;
    },
    getCarouselOffset(index) {
      const total = this.offers.length;
      let offset = index - this.activeOfferIndex;
      const half = Math.floor(total / 2);

      if (offset > half) {
        offset -= total;
      }

      if (offset < -half) {
        offset += total;
      }

      return offset;
    },
    getCarouselState(index) {
      const offset = this.getCarouselOffset(index);

      if (offset === 0) {
        return "active";
      }

      if (offset === -1) {
        return "prev";
      }

      if (offset === 1) {
        return "next";
      }

      return "hidden";
    },
    goToOffer(index) {
      this.activeOfferIndex = this.normalizeIndex(index);
    },
    nextOffer() {
      this.goToOffer(this.activeOfferIndex + 1);
    },
    previousOffer() {
      this.goToOffer(this.activeOfferIndex - 1);
    },
    startAutoplay() {
      if (
        !this.isCarouselVisible() ||
        this.autoplayTimer ||
        this.offers.length < 2
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.nextOffer();
      }, this.autoplayDelay);
    },
    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    },
    handlePointerLeave() {
      if (this.isCarouselVisible()) {
        this.startAutoplay();
      }
    },
    handleCarouselFocusOut(event) {
      const currentTarget = event.currentTarget;
      const nextTarget = event.relatedTarget;

      if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) {
        return;
      }

      if (this.isCarouselVisible()) {
        this.startAutoplay();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pricing-carousel {
  width: min(100%, 1180px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.pricing-tile {
  width: 100%;
  min-height: 100%;
  padding: clamp(22px, 2vw, 30px);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 42, 84, 0.08);
}

.pricing-tile--primary {
  background: $color-primary;
  box-shadow: 0 14px 28px rgba(0, 42, 84, 0.16),
    inset 0 1px 0 rgba(245, 236, 236, 0.06);
}

.pricing-tile--light {
  background: $color-secondary;
  box-shadow: 0 12px 24px rgba(0, 42, 84, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.pricing-carousel__stage {
  display: grid;
  grid-template-columns: 1fr;
  padding: 10px clamp(14px, 7vw, 92px) 4px;
  margin-bottom: 8px;
}

.pricing-carousel__card {
  grid-area: 1 / 1;
  width: min(100%, 470px);
  justify-self: center;
  transition: transform 0.48s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.32s ease,
    filter 0.32s ease, box-shadow 0.32s ease;
}

.pricing-carousel__card--active {
  opacity: 1;
  transform: translateX(0) scale(1);
  z-index: 3;
  pointer-events: auto;
}

.pricing-carousel__card--prev {
  opacity: 0.72;
  transform: translateX(-34%) scale(0.91);
  z-index: 2;
  pointer-events: none;
  filter: saturate(0.92);
}

.pricing-carousel__card--next {
  opacity: 0.72;
  transform: translateX(34%) scale(0.91);
  z-index: 2;
  pointer-events: none;
  filter: saturate(0.92);
}

.pricing-carousel__card--hidden {
  opacity: 0;
  transform: translateX(0) scale(0.84);
  z-index: 1;
  pointer-events: none;
}

.pricing-carousel__controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 0;
}

.pricing-carousel__nav {
  width: 50px;
  height: 50px;
  border: 0;
  border-radius: 50%;
  background: $color-primary;
  color: $color-secondary;
  font-size: 1.8rem;
  line-height: 1;
  box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.18), 0 0 20px rgba(0, 42, 84, 0.14);
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.2), 0 0 24px rgba(0, 42, 84, 0.16);
  }
}

.pricing-carousel__dots {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pricing-carousel__dot {
  width: 10px;
  height: 10px;
  border: 0;
  border-radius: 999px;
  background: rgba(0, 42, 84, 0.18);
  transition: width 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.pricing-carousel__dot--active {
  width: 34px;
  background: $color-secondary;
  box-shadow: 0 0 16px rgba(254, 185, 20, 0.22);
}

.pricing-tile__header,
.pricing-tile__price-box,
.pricing-tile__meta,
.pricing-tile__note-wrap,
.pricing-tile__highlights,
.pricing-tile__footer {
  width: 100%;
}

.pricing-tile__header {
  min-height: 88px;
}

.pricing-tile__eyebrow {
  margin: 0 0 10px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(0, 42, 84, 0.48);
  text-align: left;
}

.pricing-tile__title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
}

.pricing-tile__title {
  margin: 0;
  font-size: clamp(1.36rem, 1.14rem + 0.5vw, 1.7rem);
  line-height: 1.08;
  color: $color-primary;
  text-align: left;
}

.pricing-tile__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 6px 12px;
  border-radius: 999px;
  background: $color-secondary;
  color: $color-primary;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.24),
    0 0 16px rgba(254, 185, 20, 0.18);
}

.pricing-tile__price-box {
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px 18px;
  border-radius: 18px;
}

.pricing-tile__price {
  margin: 0;
  font-family: "Spartan", "Arial Black", Arial, sans-serif;
  font-weight: 700;
  font-size: clamp(2.5rem, 2.1rem + 0.9vw, 3rem);
  line-height: 0.95;
  text-align: left;
  color: $color-primary;

  & span {
    color: $color-secondary;
    font-size: 0.42em;
  }
}

.pricing-tile__fee {
  margin: 8px 0 0;
  min-height: 1.42em;
  font-size: 0.9rem;
  line-height: 1.42;
  text-align: left;
  color: rgba(88, 89, 91, 0.88);
}

.pricing-tile__fee--placeholder {
  visibility: hidden;
}

.pricing-tile__meta {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;

  & li {
    padding: 0;
    text-align: left;
  }
}

.pricing-tile__meta-label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0, 42, 84, 0.5);
}

.pricing-tile__meta-value {
  display: block;
  font-size: 0.96rem;
  line-height: 1.35;
  color: $color-text;
}

.pricing-tile__note-wrap {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
}

.pricing-tile__note {
  width: 100%;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.42);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(2px);
}

.pricing-tile__note-title {
  margin: 0 0 8px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: left;
  color: rgba(255, 255, 255, 0.92);
}

.pricing-tile__note-copy {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.45;
  text-align: left;
  color: $color-text-light;
}

.pricing-tile__highlights {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;

  & li {
    position: relative;
    padding-left: 18px;
    font-size: 0.98rem;
    line-height: 1.42;
    text-align: left;
    color: $color-text;

    &::before {
      content: "";
      position: absolute;
      top: 0.62rem;
      left: 0;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: $color-secondary;
    }
  }
}

.pricing-tile__footer {
  margin-top: auto;
  padding-top: 6px;
}

.pricing-tile__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 54px;
  border-radius: 16px;
  padding: 0 18px;
  background: $color-secondary;
  color: $color-primary;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.24),
    0 0 18px rgba(254, 185, 20, 0.2), 0 10px 18px rgba(0, 42, 84, 0.14);

  &:hover {
    transform: translateY(-1px);
    background: darken($color-secondary, 4%);
    color: $color-primary;
    text-decoration: none;
    box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.28),
      0 0 22px rgba(254, 185, 20, 0.24), 0 12px 22px rgba(0, 42, 84, 0.16);
  }
}

.pricing-tile--primary {
  .pricing-tile__eyebrow {
    color: rgba(245, 236, 236, 0.72);
  }

  .pricing-tile__title {
    color: $color-text-light;
  }

  .pricing-tile__badge {
    background: $color-secondary;
    color: $color-primary;
  }

  .pricing-tile__price-box {
    background: rgba(245, 236, 236, 0.04);
    box-shadow: inset 0 0 0 1px rgba(245, 236, 236, 0.08);
  }

  .pricing-tile__price {
    color: $color-text-light;
  }

  .pricing-tile__fee {
    color: rgba(245, 236, 236, 0.74);
  }

  .pricing-tile__meta-label {
    color: rgba(245, 236, 236, 0.62);
  }

  .pricing-tile__meta-value,
  .pricing-tile__highlights li {
    color: $color-text-light;
  }
}

.pricing-tile--light {
  .pricing-tile__eyebrow {
    color: rgba(0, 42, 84, 0.72);
  }

  .pricing-tile__badge {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.14), 0 0 16px rgba(0, 42, 84, 0.14);
  }

  .pricing-tile__price-box {
    background: rgba(0, 0, 0, 0.36);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18),
      inset 0 0 0 1px rgba(0, 0, 0, 0.22);
  }

  .pricing-tile__fee {
    color: rgba(255, 255, 255, 0.74);
  }

  .pricing-tile__title,
  .pricing-tile__meta-value,
  .pricing-tile__meta-label {
    color: #000000;
  }

  .pricing-tile__price {
    color: $color-text-light;
  }

  .pricing-tile__highlights li {
    color: $color-primary;
  }

  .pricing-tile__highlights li::before {
    background: $color-primary;
  }

  .pricing-tile__link {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.16), 0 0 18px rgba(0, 42, 84, 0.16),
      0 10px 18px rgba(0, 42, 84, 0.14);
  }

  .pricing-tile__link:hover {
    background: lighten($color-primary, 4%);
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.18), 0 0 22px rgba(0, 42, 84, 0.18),
      0 12px 22px rgba(0, 42, 84, 0.16);
  }
}

@media screen and (max-width: 767px) {
  .pricing-carousel {
    gap: 16px;
  }

  .pricing-carousel__stage {
    order: 2;
    display: block;
    padding: 0;
    margin-bottom: 0;
    min-height: 0 !important;
  }

  .pricing-tile {
    padding: 20px;
    border-radius: 22px;
    gap: 16px;
  }

  .pricing-carousel__card {
    display: none;
    width: 100%;
    max-width: none;
    transform: none;
    opacity: 1;
    filter: none;
  }

  .pricing-carousel__card--active {
    display: flex;
  }

  .pricing-carousel__card--prev,
  .pricing-carousel__card--next,
  .pricing-carousel__card--hidden {
    display: none;
    opacity: 0;
    transform: none;
  }

  .pricing-carousel__controls {
    order: 1;
    position: static;
    z-index: auto;
    margin-top: 0;
    min-height: 44px;
  }

  .pricing-carousel__nav {
    width: 44px;
    height: 44px;
    font-size: 1.55rem;
  }

  .pricing-tile__header {
    min-height: 0;
  }

  .pricing-tile__meta {
    grid-template-columns: 1fr;
  }

  .pricing-tile__note {
    padding: 14px 16px;
  }
}
</style>
